'use client';

import { fetchMarketingProductList } from '@/api/marketingPages';
import type { ListProduct } from '@/api/types/public';
import type { MarketingProductListResponse } from '../../../types';
import logger from '@/utils/client-logger';
import { serializeKey } from '@/utils/swr/keys';
import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

const CAPSULE_PRODUCT_PAGE_SIZE = 50;

interface CapsuleStyleProductsKeyParams {
  adp?: string;
  groupIds: number[];
  inStock: 1 | 3;
  isPreview: boolean;
  marketingPageId: number;
  soldOutSort: 1 | 2;
}

interface UseCapsuleStyleProductsParams {
  adp?: string;
  groupIds: number[];
  isPreview: boolean;
  marketingPageId: number;
  soldOutDisplay?: 1 | 2;
  soldOutSort?: 1 | 2;
}

interface CapsuleStyleProductGroupState {
  hasMore: boolean;
  loadedCount: number;
  page: number;
  products: ListProduct[];
}

type CapsuleStyleProductGroups = Record<number, CapsuleStyleProductGroupState>;

interface CapsuleStyleProductsResult {
  groupsById: CapsuleStyleProductGroups;
  hasPartialError: boolean;
}

export type CapsuleProductsByGroup = Record<number, ListProduct[]>;

function buildCapsuleStyleProductsKey(params: CapsuleStyleProductsKeyParams): string {
  return serializeKey(['capsule', 'styleProducts', params])!;
}

function getCapsuleProductKey(product: ListProduct): string | null {
  const shopSku = typeof product.shop_sku === 'string' ? product.shop_sku.trim() : '';
  if (shopSku) return `sku:${shopSku}`;
  if (product.id !== undefined && product.id !== null) return `id:${String(product.id)}`;
  return null;
}

function getResponseProducts(response?: MarketingProductListResponse): ListProduct[] {
  const products = Array.isArray(response?.items)
    ? response.items
    : Array.isArray(response?.list)
      ? response.list
      : [];

  return products.filter((product): product is ListProduct =>
    Boolean(product && typeof product === 'object'),
  );
}

function mergeUniqueProducts(currentProducts: ListProduct[], nextProducts: ListProduct[]) {
  const productKeys = new Set(
    currentProducts.map(getCapsuleProductKey).filter((key): key is string => Boolean(key)),
  );
  const mergedProducts = [...currentProducts];

  nextProducts.forEach((product) => {
    const productKey = getCapsuleProductKey(product);
    if (productKey && productKeys.has(productKey)) return;

    if (productKey) productKeys.add(productKey);
    mergedProducts.push(product);
  });

  return mergedProducts;
}

function resolveHasMore(
  response: MarketingProductListResponse,
  pageProducts: ListProduct[],
  loadedProductCount: number,
): boolean {
  if (pageProducts.length === 0) return false;
  if (typeof response.has_more === 'boolean') return response.has_more;

  const total = Number(response.total || 0);
  if (total > 0) return loadedProductCount < total;
  return pageProducts.length >= CAPSULE_PRODUCT_PAGE_SIZE;
}

async function requestCapsuleStyleProductPage(
  params: CapsuleStyleProductsKeyParams,
  groupId: number,
  page: number,
): Promise<MarketingProductListResponse> {
  return fetchMarketingProductList({
    appendCorrelation: 1,
    groupId,
    page,
    pageSize: CAPSULE_PRODUCT_PAGE_SIZE,
    pageId: params.marketingPageId,
    isPreview: params.isPreview,
    adp: params.adp,
    in_stock: params.inStock,
    sold_out_sort: params.soldOutSort,
  }) as Promise<MarketingProductListResponse>;
}

async function fetchCapsuleStyleProducts(key: string): Promise<CapsuleStyleProductsResult> {
  const [, , params] = JSON.parse(key) as [string, string, CapsuleStyleProductsKeyParams];
  const groupResults = await Promise.all(
    params.groupIds.map(async (groupId) => {
      try {
        const response = await requestCapsuleStyleProductPage(params, groupId, 1);
        const pageProducts = getResponseProducts(response);
        const mergedProducts = mergeUniqueProducts([], pageProducts);

        return {
          groupId,
          hasError: false,
          state: {
            products: mergedProducts,
            loadedCount: pageProducts.length,
            page: 1,
            hasMore: resolveHasMore(response, pageProducts, pageProducts.length),
          },
        };
      } catch (error) {
        logger.error(error, `Failed to load capsule style products for group ${groupId}, page 1:`);
        return {
          groupId,
          hasError: true,
          state: { products: [], loadedCount: 0, page: 0, hasMore: false },
        };
      }
    }),
  );

  return {
    groupsById: groupResults.reduce<CapsuleStyleProductGroups>((result, group) => {
      result[group.groupId] = group.state;
      return result;
    }, {}),
    hasPartialError: groupResults.some((group) => group.hasError),
  };
}

export function useCapsuleStyleProducts({
  groupIds,
  marketingPageId,
  isPreview,
  adp,
  soldOutDisplay,
  soldOutSort,
}: UseCapsuleStyleProductsParams) {
  const normalizedGroupIds = Array.from(
    new Set(groupIds.filter((groupId) => Number.isFinite(groupId) && groupId > 0)),
  ).sort((left, right) => left - right);
  const key =
    normalizedGroupIds.length && marketingPageId > 0
      ? buildCapsuleStyleProductsKey({
          groupIds: normalizedGroupIds,
          marketingPageId,
          isPreview,
          adp,
          inStock: soldOutDisplay === 2 ? 1 : 3,
          soldOutSort: soldOutSort === 2 ? 2 : 1,
        })
      : null;
  const { data, error, isLoading, mutate } = useSWR<CapsuleStyleProductsResult>(
    key,
    fetchCapsuleStyleProducts,
    {
      revalidateIfStale: false,
      revalidateOnReconnect: false,
    },
  );
  const dataRef = useRef(data);
  const keyRef = useRef(key);
  const mountedRef = useRef(true);
  const loadingRequestIdsRef = useRef(new Set<string>());
  const [loadingRequestIds, setLoadingRequestIds] = useState<Set<string>>(() => new Set());

  dataRef.current = data;
  keyRef.current = key;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadMoreProducts = useCallback(
    async (groupId: number) => {
      const requestKey = key;
      const currentGroup = dataRef.current?.groupsById[groupId];
      if (!requestKey || !currentGroup?.hasMore) return;

      const requestId = `${requestKey}:${groupId}`;
      if (loadingRequestIdsRef.current.has(requestId)) return;

      loadingRequestIdsRef.current.add(requestId);
      setLoadingRequestIds((current) => new Set(current).add(requestId));

      try {
        const [, , params] = JSON.parse(requestKey) as [
          string,
          string,
          CapsuleStyleProductsKeyParams,
        ];
        const nextPage = currentGroup.page + 1;
        const response = await requestCapsuleStyleProductPage(params, groupId, nextPage);
        if (!mountedRef.current || keyRef.current !== requestKey) return;

        const latestData = dataRef.current;
        const latestGroup = latestData?.groupsById[groupId];
        if (!latestData || !latestGroup || latestGroup.page !== currentGroup.page) return;

        const pageProducts = getResponseProducts(response);
        const mergedProducts = mergeUniqueProducts(latestGroup.products, pageProducts);
        const loadedCount =
          (latestGroup.loadedCount ?? latestGroup.products.length) + pageProducts.length;
        const nextData: CapsuleStyleProductsResult = {
          ...latestData,
          groupsById: {
            ...latestData.groupsById,
            [groupId]: {
              products: mergedProducts,
              loadedCount,
              page: nextPage,
              hasMore: resolveHasMore(response, pageProducts, loadedCount),
            },
          },
        };

        dataRef.current = nextData;
        await mutate(nextData, { revalidate: false });
      } catch (loadMoreError) {
        if (!mountedRef.current || keyRef.current !== requestKey) return;

        logger.error(
          loadMoreError,
          `Failed to load capsule style products for group ${groupId}, next page:`,
        );
        const latestData = dataRef.current;
        if (latestData) {
          const nextData = { ...latestData, hasPartialError: true };
          dataRef.current = nextData;
          await mutate(nextData, { revalidate: false });
        }
      } finally {
        loadingRequestIdsRef.current.delete(requestId);
        if (mountedRef.current) {
          setLoadingRequestIds((current) => {
            const next = new Set(current);
            next.delete(requestId);
            return next;
          });
        }
      }
    },
    [key, mutate],
  );

  const groupsById = data?.groupsById || {};
  const productsByGroup = normalizedGroupIds.reduce<CapsuleProductsByGroup>((result, groupId) => {
    result[groupId] = groupsById[groupId]?.products || [];
    return result;
  }, {});
  const hasMoreByGroup = normalizedGroupIds.reduce<Record<number, boolean>>((result, groupId) => {
    result[groupId] = Boolean(groupsById[groupId]?.hasMore);
    return result;
  }, {});
  const loadingMoreByGroup = normalizedGroupIds.reduce<Record<number, boolean>>(
    (result, groupId) => {
      result[groupId] = Boolean(key && loadingRequestIds.has(`${key}:${groupId}`));
      return result;
    },
    {},
  );

  return {
    productsByGroup,
    hasMoreByGroup,
    loadingMoreByGroup,
    loadMoreProducts,
    isLoading,
    hasError: Boolean(error || data?.hasPartialError),
  };
}
